/**
 * Centralized page/feature access-check helpers.
 *
 * Two comparable "completeness statuses" a page/nav item/settings entry can carry:
 * - "demo": Orderly-internal only. Gated by the current user's email domain. NOT signposted
 *   in the UI (no badge/chip) — it's purely a visibility gate for Orderly staff.
 * - "preview": Customer-visible. Gated by environment (test/qa/uat/localhost) or the Admin
 *   permission. Always signposted with a "Preview" badge/chip wherever it's shown.
 *
 * Both Settings (src/pages/Settings/index.tsx) and the nav drawer
 * (src/components/ui/drawer/**) call the same `canAccessPageStatus` helper so new pages of
 * varying completeness are gated consistently in both places.
 */

export type PageAccessStatus = 'demo' | 'preview';

/* ---- Demo (Orderly-internal, opt-in, not signposted) ---- */

const DEMO_MODE_STORAGE_KEY = 'msw-enabled';

/** Email domains allowed to see/use Demo Mode. */
const DEMO_ALLOWED_DOMAINS = ['orderly.io', 'kudosqa.com'];

/**
 * Returns true if the given email/hostname combination is allowed to opt into Demo Mode.
 */
export function isDemoModeEligible(email: string, hostname: string): boolean {
  if (!email) return false;
  const normalizedHostname = hostname.toLowerCase();
  const environmentEligible = normalizedHostname === 'localhost' || normalizedHostname.includes('uat');
  const domain = email.split('@')[1]?.toLowerCase();
  return environmentEligible && DEMO_ALLOWED_DOMAINS.includes(domain);
}

/** Returns true if the user has manually opted into Demo Mode. */
export function isDemoModeEnabled(): boolean {
  return localStorage.getItem(DEMO_MODE_STORAGE_KEY) === 'true';
}

/** Enables Demo Mode (persists to localStorage). Call after successful login when opted in. */
export function enableDemoMode(): void {
  localStorage.setItem(DEMO_MODE_STORAGE_KEY, 'true');
}

/** Disables Demo Mode (removes from localStorage). Call on logout. */
export function disableDemoMode(): void {
  localStorage.removeItem(DEMO_MODE_STORAGE_KEY);
}

/* ---- Preview (customer-visible, environment/permission driven, signposted) ---- */

const PREVIEW_ALLOWED_HOSTNAMES = ['localhost', '127.0.0.1', '::1'];
const PREVIEW_ALLOWED_HOSTNAME_FRAGMENTS = ['test', 'qa', 'uat'];

/** Returns true if the given hostname is a non-production environment eligible for Preview. */
export function isPreviewEligibleHostname(hostname: string): boolean {
  const normalizedHostname = hostname.toLowerCase();
  return PREVIEW_ALLOWED_HOSTNAMES.includes(normalizedHostname)
    || PREVIEW_ALLOWED_HOSTNAME_FRAGMENTS.some((fragment) => normalizedHostname.includes(fragment));
}

/**
 * Returns true if Preview content should be visible: either the user is an Admin (any
 * environment), or the current environment is a non-production one.
 */
export function isPreviewEligible(hostname: string | undefined, hasAdminPermission: boolean): boolean {
  if (hasAdminPermission) return true;
  if (!hostname) return false;
  return isPreviewEligibleHostname(hostname);
}

export type PreviewSwapMode = 'both' | 'legacy' | 'preview';

/**
 * Resolves a configuration-backed preview swap. Preview environments expose both versions so
 * testing never depends on store configuration; production exposes exactly one version, except
 * Admins get both versions in production while the store's configuration is disabled, reusing
 * the same production showcase carve-out as `isPreviewEligible`.
 */
export function resolvePreviewSwap(
  hostname: string | undefined,
  isPreviewEnabled: boolean,
  hasAdminPermission = false,
): PreviewSwapMode {
  if (hostname && isPreviewEligibleHostname(hostname)) return 'both';
  if (isPreviewEnabled) return 'preview';
  return hasAdminPermission ? 'both' : 'legacy';
}

/* ---- Demo-only permission bypass (fixes internal page-level permission checks) ---- */

/**
 * Permission-name prefixes that belong exclusively to demo-flagged domains (no real backend).
 * Pages under these domains call `hasPermissionTo(...)` internally (separately from the
 * route/nav-level `status: 'demo'` bypass) to gate buttons/tabs/detail views — this list lets
 * `hasPermissionTo` (see useUser.tsx) bypass those checks too when Demo Mode is enabled, without
 * weakening permission checks for any real/production permission.
 */
const DEMO_ONLY_PERMISSION_PREFIXES = [
  'CashManagement',
  'CashReconciliation',
  'OperationalDays',
  'TaskSchedules',
  'TaskTemplates',
  'TaskCategories',
  'AdvancedTasks',
  'AutomatedWorkflows',
  'Documents',
  'RecipeV2',
  'NewsManagement',
];

/** Returns true if the given permission name belongs exclusively to a demo-flagged domain. */
export function isDemoOnlyPermission(permission: string): boolean {
  return DEMO_ONLY_PERMISSION_PREFIXES.some((prefix) => permission.startsWith(prefix));
}

/* ---- Unified dispatcher used by Settings + Drawer ---- */

export interface PageAccessContext {
  hostname?: string;
  hasAdminPermission?: boolean;
  hasPreviewConfiguration?: boolean;
}

/**
 * Single entry point for gating a page/nav item/settings entry by its completeness status.
 * Returns true (visible) when status is undefined — i.e. fully released pages are unaffected.
 * Preview content is visible in Demo Mode, eligible environments, to Admin users, or when its
 * backing configuration is enabled. See `shouldShowPreviewTag` for the separate badge decision.
 */
export function canAccessPageStatus(status: PageAccessStatus | undefined, context: PageAccessContext): boolean {
  if (!status) return true;
  if (status === 'demo') return isDemoModeEnabled();
  return isDemoModeEnabled() || context.hasPreviewConfiguration === true ||
    isPreviewEligible(context.hostname, context.hasAdminPermission ?? false);
}

/**
 * Returns true if the "Preview" badge/chip should be shown for a preview-flagged item. Preview
 * screens remain visible while Demo Mode is enabled (see `canAccessPageStatus`), but the tag
 * itself should be suppressed in that case — Demo Mode is internal-only and shouldn't surface a
 * customer-facing "Preview" label.
 */
export function shouldShowPreviewTag(status: PageAccessStatus | undefined): boolean {
  return status === 'preview' && !isDemoModeEnabled();
}

/**
 * Inverse of `canAccessPageStatus`: some pages/items should be HIDDEN while Demo Mode is
 * enabled (e.g. a real page that's superseded by a demo equivalent, or a preview section that
 * would be confusing to show alongside demo content). Use the `hideInDemo` flag on a
 * `DrawerNavItem`/`SettingItem`/`SettingSection` for this — it takes priority over any other
 * visibility check.
 */
export function shouldHideInDemo(hideInDemo: boolean | undefined): boolean {
  return !!hideInDemo && isDemoModeEnabled();
}
